import React, { useEffect, useState } from 'react';
import { CalendarBlank, CaretDown, CheckCircle, FileText, SunHorizon, Warning } from '@phosphor-icons/react';
import { istKritisch, type BriefingMail } from '../../lib/dashboard/briefing';
import { supabase } from '../../lib/supabase/client';
import { lokalerTagesbereich } from '../../lib/kalender/zeit';
import { ladeEinheitenLeerstand } from '../../lib/dashboard/leerstand';
import { istMeineZustaendigkeit, type DashboardScope, type ScopeNutzer } from '../../lib/dashboard/scope';
import { DashboardWidget, KopfKachel } from './DashboardWidget';

interface Props {
  mails: BriefingMail[];
  onOpenEmail: (id: string) => void;
  onNavigate?: (path: string) => void;
  scope: DashboardScope;
  nutzer: ScopeNutzer;
  /** Mitarbeitende: keine firmenweiten Info-Zeilen, fremde Notfälle bleiben außen vor. */
  nurEigene?: boolean;
  /** Rasterspalte (z. B. 'span 6') */
  spalte?: string;
}

interface InfoDaten {
  termineHeute: { id: string; titel: string }[];
  auslaufendeVertraege: number;
  leerstandFrei: number;
  leerstandGesamt: number;
}

const heuteIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

interface InfoZeile {
  key: string;
  text: string;
  pfad: string;
}

const BriefingKopf: React.FC<Props> = ({ mails, onOpenEmail, onNavigate, scope, nutzer, nurEigene, spalte }) => {
  const heute = heuteIso();
  // Notfälle erscheinen im Briefing weiterhin — für Mitarbeitende aber nur, solange sie
  // niemandem zugewiesen sind. Der Notfall einer Kollegin ist nicht ihre Aufgabe.
  const imScope = (m: BriefingMail) =>
    scope === 'alle'
    || istMeineZustaendigkeit(m.eff_zustaendig_id, m.eff_zustaendig_typ, nutzer)
    || (m.ist_notfall && (!nurEigene || !m.eff_zustaendig_id));
  const relevante = mails.filter(imScope);
  const kritisch = relevante.filter((m) => istKritisch(m, heute));
  const sichtbar = kritisch.slice(0, 5);
  const rest = kritisch.length - sichtbar.length;

  const [info, setInfo] = useState<InfoDaten | null>(null);

  useEffect(() => {
    // Termine und auslaufende Verträge sind firmenweite Zahlen — für Mitarbeitende gar
    // nicht erst laden.
    if (nurEigene) return;
    let cancelled = false;
    const laden = async () => {
      try {
        const jetzt = new Date();
        const { startIso, endeIso } = lokalerTagesbereich(jetzt);
        const heuteLokal = heuteIso();
        const in30 = new Date(jetzt);
        in30.setDate(in30.getDate() + 30);
        const in30Lokal = `${in30.getFullYear()}-${String(in30.getMonth() + 1).padStart(2, '0')}-${String(in30.getDate()).padStart(2, '0')}`;

        const [termineRes, vertraegeRes, leerstand] = await Promise.all([
          // Abgesagte Termine nicht mitzählen (wie TermineWocheWidget.tsx u. a.).
          supabase.from('termine').select('id, titel, start_datum')
            .gte('start_datum', startIso)
            .lte('start_datum', endeIso)
            .not('status', 'eq', 'abgesagt')
            .order('start_datum', { ascending: true }),
          supabase.from('vertraege').select('id, ende')
            .gte('ende', heuteLokal)
            .lte('ende', in30Lokal),
          // Leerstand auf Einheiten-Ebene — gleicher Loader wie LeerstandWidget.tsx.
          ladeEinheitenLeerstand(),
        ]);

        if (cancelled) return;
        setInfo({
          termineHeute: termineRes.data || [],
          auslaufendeVertraege: (vertraegeRes.data || []).length,
          leerstandFrei: leerstand.frei,
          leerstandGesamt: leerstand.gesamt,
        });
      } catch (err) {
        console.error('Briefing-Info Fehler:', err);
      }
    };
    laden();
    return () => { cancelled = true; };
  }, [nurEigene]);

  const infoZeilen: InfoZeile[] = [];
  if (info) {
    if (info.termineHeute.length > 0) {
      const namen = info.termineHeute.slice(0, 2).map((t) => t.titel).join(', ');
      const mehr = info.termineHeute.length > 2 ? '…' : '';
      infoZeilen.push({
        key: 'termine',
        text: `${info.termineHeute.length} ${info.termineHeute.length === 1 ? 'Termin' : 'Termine'} heute: ${namen}${mehr}`,
        pfad: '/admin/kalender',
      });
    }
    if (info.auslaufendeVertraege > 0) {
      infoZeilen.push({
        key: 'vertraege',
        text: info.auslaufendeVertraege === 1
          ? '1 Vertrag endet in ≤ 30 Tagen'
          : `${info.auslaufendeVertraege} Verträge enden in ≤ 30 Tagen`,
        pfad: '/admin/vertraege',
      });
    }
    // Leerstand bewusst NICHT im Briefing (User-Wunsch 2026-07-28: fürs Tagesgeschäft
    // nicht relevant) — die Zahlen bleiben über die Objekt-Seiten einsehbar.
  }

  const InfoZeilenListe = infoZeilen.length > 0 ? (
    <div className="flex flex-wrap" style={{ gap: 8, paddingTop: kritisch.length > 0 ? 12 : 0, borderTop: kritisch.length > 0 ? '1px solid var(--line)' : 'none' }}>
      {infoZeilen.map((z) => {
        const I = z.key === 'termine' ? CalendarBlank : FileText;
        return (
          <button key={z.key} type="button" onClick={() => onNavigate?.(z.pfad)}
            data-tip={z.key === 'termine' ? 'Termine heute – Zum Kalender' : 'Auslaufende Verträge – Zur Vertragsübersicht'}
            className="flex min-w-0 items-center text-left hover:!bg-[var(--hover)] hover:!text-[var(--acc)]"
            style={{ minHeight: 36, padding: '6px 12px', gap: 8, borderRadius: 18, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
              background: 'var(--fill)', color: 'var(--ink)', boxShadow: 'inset 0 0 0 1px var(--line)' }}>
            <I size={16} weight="fill" color="var(--acc)" className="shrink-0" />
            <span className="truncate">{z.text}</span>
          </button>
        );
      })}
    </div>
  ) : null;

  return (
    <DashboardWidget farbe="--dw-orange" icon={SunHorizon} ariaLabel="Tages-Briefing" spalte={spalte} stapel style={{ marginBottom: 20 }}>
      {kritisch.length > 0 ? (
        <div className="flex flex-col" style={{ gap: 10 }}>
          <div className="flex items-center" style={{ gap: 10 }}>
            <KopfKachel farbe="--dw-rot" icon={Warning} />
            <h3 className="m-0" style={{ fontSize: 14, fontWeight: 800, letterSpacing: '-0.02em' }}>Tages-Briefing</h3>
            <span data-tip="Kritisch – Notfall, Priorität dringend oder Frist heute bzw. überschritten" className="inline-flex items-center"
              style={{ height: 24, padding: '0 10px', borderRadius: 12, fontSize: 12, fontWeight: 800, background: 'var(--bad-soft)', color: 'var(--bad)' }}>
              {kritisch.length} kritisch
            </span>
          </div>
          <div className="flex flex-col" style={{ gap: 2 }}>
            {sichtbar.map((m) => (
              <button key={m.id} type="button" onClick={() => onOpenEmail(m.id)} data-tip="E-Mail öffnen – Springt in den E-Mail-Bereich zu dieser Nachricht"
                className="flex flex-wrap items-center text-left hover:!bg-[var(--hover)]"
                style={{ minHeight: 34, padding: '6px 10px', gap: 10, borderRadius: 12, border: 'none', cursor: 'pointer', fontFamily: 'inherit', background: 'transparent', color: 'var(--ink)' }}>
                <span className="shrink-0" style={{ width: 8, height: 8, borderRadius: '50%', background: m.ist_notfall || m.prioritaet === 'dringend' ? 'var(--bad)' : 'var(--warn)' }} />
                <span className="truncate" style={{ flex: 1, minWidth: 160, fontSize: 14, fontWeight: 700 }}>{m.erkannte_aufgabe || m.betreff}</span>
                {scope === 'meine' && m.ist_notfall && !m.eff_zustaendig_id && (
                  <span className="inline-flex shrink-0 items-center" style={{ height: 22, padding: '0 8px', borderRadius: 11, fontSize: 11, fontWeight: 800, background: 'var(--warn-soft)', color: 'var(--warn)' }}>nicht zugewiesen</span>
                )}
                <span className="flex min-w-0 items-center" style={{ gap: 6, fontSize: 12.5, fontWeight: 600, color: 'var(--ink3)' }}>
                  <span className="truncate">{m.objekt_name || m.absender_name || m.von}</span>
                  {m.frist_datum && m.frist_datum <= heute && (
                    <span data-tip="Frist – Von der KI erkannte Frist" className="inline-flex shrink-0 items-center"
                      style={{ height: 20, padding: '0 7px', borderRadius: 10, fontSize: 11, fontWeight: 800, background: 'var(--bad-soft)', color: 'var(--bad)' }}>Frist!</span>
                  )}
                </span>
              </button>
            ))}
            {rest > 0 && (
              <button type="button" onClick={() => document.getElementById('email-assistent')?.scrollIntoView({ behavior: 'smooth' })}
                className="flex items-center self-start"
                style={{ height: 32, padding: '0 10px', gap: 6, borderRadius: 10, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 700, background: 'transparent', color: 'var(--bad)' }}>
                <CaretDown size={12} weight="bold" /> +{rest} weitere kritische Mails
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="flex items-center" style={{ gap: 10 }}>
          <KopfKachel farbe="--dw-gruen" icon={CheckCircle} />
          <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink2)' }}>Keine dringenden Vorgänge</span>
        </div>
      )}
      {InfoZeilenListe}
    </DashboardWidget>
  );
};

export default BriefingKopf;
